/* eslint-disable no-console */
/* eslint-disable react/display-name */
/* eslint-disable react-hooks/exhaustive-deps */
import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import Table from 'apollo-react/components/Table';
import ListNumber from 'apollo-react-icons/ListNumber';
import Plus from 'apollo-react-icons/Plus';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ActionCell from './ActionCell';
import { getColumnFCM } from './ColumnFCM';
import CompareBuildsButton from './CompareBuildsButton';
import CompareBuildsCell from './CompareBuildsCell';
import CustomCell from './CustomCell';
import {
  useAddingInitialRows,
  useFormSelector,
  useGridTitle,
  useInitializingGridFromDatasource,
} from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import { getIsRtlLanguage } from '../../../selectors/AuthSelectors';
import { getUITA, isTrue } from '../../../utils/commonUtils';
import {
  endTimer,
  getTimer,
  HIGH_VOLUME_DATA_TIMER,
  PAGE_LOAD_TIMER,
  startTimer,
} from '../../../utils/log';
import { SELECTED_TABLE_ROW_STYLES } from '../../../utils/themeUtils';
import {
  addRow,
  bulkUpdateFormData,
  checkActiveContainers,
  copyRow,
  deleteFormDataRow,
  initializeHighVolumeGrid,
  removeSelectedSubGrid,
  setSelectedSubGrids,
  setUserMadeInteraction,
} from '../../actions/runtime.actions';
import Accordion from '../../components/Accordion';
import AddDocumentModal from '../../components/AddDocumentModal';
import AttachmentModal from '../../components/AttachmentModal';
import SortPrintOrderModal from '../../components/SortPrintOrderModal';
import {
  ATTACHMENT_TYPES,
  DEFAULT_ROWS_PER_PAGE,
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../../constants/form-manager.constants';
import { SSGridContext, SSModalContext } from '../../context';
import { getIsFormChangesMode } from '../../selectors/common.selectors';
import {
  getClosestParentWithEntityName,
  getEvaluatedConditions,
  getEvaluatedExpression,
  getIsDownloadInOfflineAvailableForEntity,
  getIsParentlessTable,
} from '../../selectors/form-manager.selectors';
import { getSelectedSubGridsWithActualIndexes } from '../../selectors/runtime.selectors';
import {
  getEvaluatedTableRowsProperties,
  getFilteredRows,
  getGridColumns,
  getHiddenColumnsMap,
  getRowIdFromEntityData,
  getSortData,
  getSubGridByGridId,
  isHighVolumeGrid,
} from '../../selectors/ssGrid.selectors';
import {
  createSelectedSubGridObject,
  getFilterComponentByDataType,
  getFilterFunctionByDataType,
  getHighestPrintOrderNumber,
  getInModalTitle,
  getSortFunction,
  getTablePaginationProps,
} from '../../utils/controls/ssGrid.utils';
import { equalityFn } from '../../utils/form-manager.utils';
import { getIsEditMode, getTableRowsEqualityFn } from '../../utils/runtime.utils';
import SSSubGrid from '../ssSubGrid';

const useStyles = makeStyles({
  root: {
    border: 'solid 1px #e9e9e9',
  },
  emptyTable: {
    '& tbody tr td': {
      height: '80px !important',
    },
  },
  rowSpacing: {
    '& tbody tr td': {
      paddingTop: 5,
      paddingBottom: 5,
    },
  },
  headerButtons: {
    textAlign: 'right',
    paddingTop: '8px !important',
  },
  selected: SELECTED_TABLE_ROW_STYLES,
  sortPrintOrderButton: {
    marginRight: 10,
  },
});

const SSRuntimeGrid = ({
  id,
  index,
  entityName,
  collapsible = true,
  childrenNodes,
  gridType,
  isDetail,
  allowSort,
  sort,
  formKey,
  isViewOnly,
  expandableRows,
  initialRows,
  disablePagination = false,
  isOnline,
  instanceId,
  parentIndex,
}) => {
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const tableRowHeadRef = useRef(null);
  const containerRef = useRef(null);
  const isNewRowAddedRef = useRef(false);
  const isFiltersShownRef = useRef(false);
  const classes = useStyles();
  const ssModalContext = useContext(SSModalContext);
  const { isInModal, mappingValues, targetControlId } = ssModalContext;
  const isParentlessTable = useFormSelector(getIsParentlessTable, { id });
  const parentEntityName = useFormSelector(getClosestParentWithEntityName, { id });
  const isChildAttachment = gridType === 'ChildAttachment';
  const parentRowId = useFormSelector(getRowIdFromEntityData, {
    entityName: parentEntityName,
    index: parentIndex,
    isChildAttachment,
  });
  const selectorProps = {
    id,
    index,
    entityName,
    isParentlessTable,
    mappingValues,
    targetControlId,
    parentEntityName,
    parentRowId,
  };
  const title = useGridTitle(selectorProps);
  const disableAdd = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.DISABLE_ADD,
  });
  const editAllRows = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.EDIT_ALL_ROWS,
    defaultValue: true,
  });
  const disableDeleteFilter = useFormSelector(
    getEvaluatedTableRowsProperties,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.DISABLE_DELETE,
    },
    equalityFn
  );
  const disableEditFilter = useFormSelector(
    getEvaluatedTableRowsProperties,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.DISABLE_EDIT,
    },
    equalityFn
  );
  const enableCopyFilter = useFormSelector(
    getEvaluatedTableRowsProperties,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.ENABLE_COPY,
    },
    equalityFn
  );
  const header = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.HEADER,
    defaultValue: true,
  });
  const [invalidateRows, setInvalidateRows] = useState(false);
  const columns = useFormSelector(getGridColumns, { childrenNodes }, equalityFn);
  const rows = useFormSelector(
    getFilteredRows,
    { ...selectorProps, columns, translateString },
    getTableRowsEqualityFn(isParentlessTable, invalidateRows || isFiltersShownRef.current)
  );
  const hiddenColumnsMap = useFormSelector(
    getHiddenColumnsMap,
    { childrenNodes, index, entityName },
    equalityFn
  );
  const sortData = useFormSelector(getSortData, { sort }, equalityFn);
  const isHighVolumeGridData = useFormSelector(isHighVolumeGrid, { entityName });
  const selectedSubGrids = useFormSelector(getSelectedSubGridsWithActualIndexes, {}, equalityFn);
  const subgridId = useFormSelector(getSubGridByGridId, { id });
  const isDownloadInOfflineAvailableForEntity = useFormSelector(
    getIsDownloadInOfflineAvailableForEntity
  );
  const isFormChangesMode = useFormSelector(getIsFormChangesMode);

  const { isMobileDevice } = useWindowDimensions();

  const [expandedRows, setExpandedRows] = useState([]);
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [openAddDocumentModal, setOpenAddDocumentModal] = useState(false);
  const [openSortPrintOrderModal, setOpenSortPrintOrderModal] = useState(false);
  const [page, setPage] = useState(undefined);
  const [isCollapsed, setCollapsed] = useState(isMobileDevice && !isInModal);
  const [isLoading, setIsLoading] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [selectedBuilds, setSelectedBuilds] = useState([]);

  const rowIndexKey = isParentlessTable ? 'gridArrIndex' : 'index';
  const rowsLength = rows.length;
  const selectedSubGridProps = {
    subgridId,
    isDetail,
    title,
    isViewOnly,
    ssModalContext,
    disableEditFilter,
    parentGridInstanceId: instanceId,
    translateString,
  };
  const showHeader = isTrue(header);
  const disableOfflineAttachmentButton =
    !isOnline && ATTACHMENT_TYPES.includes(gridType) && !isDownloadInOfflineAvailableForEntity;
  const isContentHistory = gridType === 'CompareContentHistory';
  const isBatchDocuments = gridType === 'BatchDocuments';
  const showAddButton =
    !disableAdd && !isDetail && !isViewOnly && !disableOfflineAttachmentButton && !isBatchDocuments;
  const showHeaderButtons = showAddButton || isContentHistory || isBatchDocuments;
  const sortProps = isTrue(allowSort)
    ? {
        initialSortedColumn: sortData ? sortData.accessor : columns[0].accessor,
        initialSortOrder: sortData ? sortData.direction : 'asc',
      }
    : {};
  const useExpandableRows =
    (isTrue(expandableRows) && !isMobileDevice) ||
    (isInModal && !isMobileDevice) ||
    formKey === FORM_KEYS.MODAL;

  const highestPrintOrderNubmer = useMemo(
    () => getHighestPrintOrderNumber(isBatchDocuments, rows),
    [isBatchDocuments, rows]
  );

  const columnsWithCustomCells = useMemo(() => {
    const tempColumns = columns.map((column) => ({
      ...column,
      header: isFormChangesMode
        ? getColumnFCM(column.columnKey, column.header, hiddenColumnsMap[column.columnKey])
        : column.header,
      isParentlessTable,
      subgridId,
      index: isChildAttachment ? [0] : index,
      isDetail,
      hidden: hiddenColumnsMap[column.columnKey] && !isFormChangesMode,
      isColumnHiddenFCM: hiddenColumnsMap[column.columnKey] && isFormChangesMode,
      sortFunction: isFormChangesMode ? undefined : getSortFunction(column.dataType),
      isViewOnly,
      customCell: CustomCell,
      filterFunction: getFilterFunctionByDataType(column),
      filterComponent: getFilterComponentByDataType(column),
    }));

    if (isContentHistory) {
      tempColumns.unshift({
        key: 'content-history',
        accessor: 'COMPARE',
        customCell: CompareBuildsCell,
        selectedBuilds,
        setSelectedBuilds,
      });
    }

    const actions = {};

    if (!isDetail && !isViewOnly) {
      actions.handleDelete = (row) => {
        batch(() => {
          dispatch(setUserMadeInteraction(formKey));
          dispatch(
            deleteFormDataRow({
              rowIndex: isParentlessTable ? _.last(row.gridArrIndex) : row.index,
              index: isParentlessTable ? row.gridArrIndex : isChildAttachment ? [0] : index,
              entityName,
              formKey,
            })
          );

          if (useExpandableRows && expandedRows.length) {
            setExpandedRows([]);
          } else if (selectedSubGrids.some(({ rowId }) => rowId === row.key)) {
            dispatch(removeSelectedSubGrid(row.key, formKey));
          }
        });

        if (selectedBuilds.length) {
          setSelectedBuilds((prev) => prev.filter((build) => build.key !== row.key));
        }

        if (subgridId) {
          dispatch(checkActiveContainers(formKey));
        }

        if (!subgridId && !isTrue(editAllRows)) {
          setEditedRows((prevState) => {
            const newState = { ...prevState };
            delete newState[row.key];
            return newState;
          });
        }
      };

      actions.handleCopy = (row) => {
        dispatch(setUserMadeInteraction(formKey));

        const newRowId = uuidv4();

        dispatch(
          copyRow({
            newRowId,
            rowIndex: isParentlessTable ? _.last(row.gridArrIndex) : row.index,
            gridIndex: isParentlessTable ? row.gridArrIndex : index,
            gridId: id,
            formKey,
            isParentlessTable,
          })
        );

        if (!subgridId && !isTrue(editAllRows)) {
          setEditedRows((prevState) => ({ ...prevState, [newRowId]: { editMode: true } }));
        }
      };
    }
    if (!subgridId && !isTrue(editAllRows) && !isViewOnly) {
      actions.handleDone = (row) => {
        setEditedRows((prevState) => ({ ...prevState, [row.key]: { editMode: false } }));
      };
      actions.handleEdit = (row) => {
        setEditedRows((prevState) => ({ ...prevState, [row.key]: { editMode: true } }));
      };
    }

    if (subgridId) {
      actions.handleEdit = (row) => {
        if (useExpandableRows) {
          setExpandedRows(
            expandedRows.includes(`${row[rowIndexKey]}`) ? [] : [`${row[rowIndexKey]}`]
          );
          dispatch(checkActiveContainers(formKey));
          return;
        }

        if (selectedSubGrids.some(({ rowId }) => rowId === row.key)) {
          dispatch(removeSelectedSubGrid(row.key, formKey));
        } else {
          dispatch(
            setSelectedSubGrids(
              createSelectedSubGridObject({
                row,
                ...selectedSubGridProps,
              }),
              formKey
            )
          );
        }

        dispatch(checkActiveContainers(formKey));
      };
    }

    if (Object.keys(actions).length) {
      tempColumns.push({
        index,
        subgridId,
        expandedRows,
        selectedSubGrids,
        useExpandableRows,
        isParentlessTable,
        rowIndexKey,
        accessor: 'actions',
        ...actions,
        align: 'right',
        key: 'actions',
        customCell: ActionCell,
      });
    }

    // handle sort of not visisble columns
    const sortColumnInHeader = tempColumns.some((column) => column.accessor === sortData?.accessor);

    if (sortData && !sortColumnInHeader) {
      tempColumns.push({
        index,
        accessor: sortData.accessor,
        hidden: true,
        key: 'sort',
        sortFunction: getSortFunction(sortData.dataType),
      });
    }

    return tempColumns;
  }, [
    JSON.stringify(index),
    columns,
    sortData,
    expandedRows,
    selectedSubGrids,
    hiddenColumnsMap,
    disableEditFilter,
    title,
    isViewOnly,
    selectedBuilds,
    isFormChangesMode,
    translateString,
  ]);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      const pageLoadTimer = getTimer(PAGE_LOAD_TIMER);
      console.log(`🕑 Partial page load: ${pageLoadTimer}s`);
      startTimer(HIGH_VOLUME_DATA_TIMER);

      await dispatch(initializeHighVolumeGrid({ index, entityName, formKey }));

      const time = endTimer(HIGH_VOLUME_DATA_TIMER);
      console.log(`🕑 High volume data loaded [${entityName}]: ${time}s`);

      setIsLoading(false);
    };

    if (isHighVolumeGridData) {
      initialize();
    }
  }, []);

  useAddingInitialRows({
    initialRows,
    selectorProps,
  });

  useInitializingGridFromDatasource({
    rowsLength,
    selectorProps,
  });

  useEffect(() => {
    if (isRtlLanguage) {
      const applyAttributeTimer = setTimeout(() => {
        return tableRowHeadRef.current?.columnHeaderRootRef.current.setAttribute('dir', 'ltr');
      }, 1000);
      return () => clearTimeout(applyAttributeTimer);
    }
  }, [isRtlLanguage]);

  useEffect(() => {
    if (invalidateRows) {
      setInvalidateRows(false);
    }
  }, [invalidateRows]);

  useEffect(() => {
    if (subgridId && isNewRowAddedRef.current) {
      isNewRowAddedRef.current = false;

      // Expand newly added row
      setTimeout(() => {
        if (useExpandableRows) {
          setExpandedRows([`${rows[0][rowIndexKey]}`]);
        } else {
          dispatch(
            setSelectedSubGrids(
              createSelectedSubGridObject({
                row: { ...rows[0], entityName, rowsLength, uiRowIndex: 0 },
                ...selectedSubGridProps,
              }),
              formKey
            )
          );
        }

        dispatch(checkActiveContainers(formKey));
      }, 200);
    }
  });

  const handleAddClick = useCallback(() => {
    if (ATTACHMENT_TYPES.includes(gridType)) {
      setOpenAttachmentModal(true);
    } else {
      batch(() => {
        dispatch(setUserMadeInteraction(formKey));

        const newRowId = uuidv4();
        // ContainerInstanceIdForHandMadeNewObject is used to mark that the row was added by clicking on 'Add' button
        dispatch(
          addRow({
            entityName,
            index,
            initialRowData: { ContainerInstanceIdForHandMadeNewObject: id, _newRowId: newRowId },
            formKey,
            isParentlessTable,
          })
        );

        if (!subgridId && !isTrue(editAllRows)) {
          setEditedRows((prevState) => ({ ...prevState, [newRowId]: { editMode: true } }));
        }

        if (isInModal) {
          const changesArray = Object.entries(mappingValues).map(([updateTo, value]) => {
            const [entityName, fieldName] = updateTo.split('.');
            return { entityName, fieldName, value, index: [0] };
          });

          dispatch(bulkUpdateFormData(changesArray, formKey));
        }
      });

      isNewRowAddedRef.current = true;

      // Navigate to first page
      setPage(0);
      // Reset naviation to table component
      // TODO: Remove when the Apollo table handles the navigation
      setTimeout(() => {
        setPage(undefined);
      }, 500);
    }
  }, [JSON.stringify(index), entityName, mappingValues]);

  // declare expandableComponent injecting row
  const expandableComponent = useCallback(
    ({ row }) => (
      <SSSubGrid
        subgridId={subgridId}
        index={isChildAttachment ? [0] : index}
        row={row}
        isDetail={isDetail}
        viewOnly={isViewOnly || disableEditFilter[row[rowIndexKey]]}
        isParentlessTable={isParentlessTable}
      />
    ),
    [JSON.stringify(index), rowsLength, isViewOnly, JSON.stringify(disableEditFilter)]
  );

  const enablePagination = rowsLength > DEFAULT_ROWS_PER_PAGE && !isTrue(disablePagination);

  // layout effect to hide empty row
  useLayoutEffect(() => {
    const table = containerRef?.current;
    if (table) {
      const gridRows = table.querySelectorAll('tbody tr:not([ui-ta="grid-row"])');
      if (gridRows.length) {
        const emptyRow = gridRows[gridRows.length - 1];
        // Apollo lib empty row has height style set, this is the only thing that differentiate
        // expandable rows of empty rows
        if (emptyRow.style.height) {
          emptyRow.style.display =
            rowsLength > 0 && rowsLength < DEFAULT_ROWS_PER_PAGE ? 'none' : 'table-row';
        }
      }
    }
  }, [rowsLength, containerRef]);

  return (
    <>
      <div
        style={{ marginTop: 6 }}
        ui-ta={getUITA('FormPart', subgridId ? 'SSComplexGrid' : 'SSGrid', entityName)}
        data-ss-id={id}
      >
        <SSGridContext.Provider value={{ rows, columns }}>
          <Accordion
            isCollapsed={isCollapsed}
            showHeader={showHeader}
            controlType={SS_CONTROL_TYPE.GRID}
            title={isInModal ? getInModalTitle(title, rowsLength) : title}
            onChange={() => collapsible && setCollapsed(!isCollapsed)}
            autoScrollUpEnabled={isMobileDevice && isCollapsed && collapsible}
          >
            {isLoading && <Loader isInner />}
            <Grid container spacing={1}>
              {showHeaderButtons && (
                <Grid classes={{ item: classes.headerButtons }} item xs={12}>
                  {isContentHistory && (
                    <CompareBuildsButton
                      selectedBuilds={selectedBuilds}
                      showAddButton={showAddButton}
                    />
                  )}
                  {showAddButton && (
                    <span ui-ta={getUITA('FormCtrl', 'AddButton')}>
                      <Button color="primary" variant="secondary" onClick={handleAddClick}>
                        <Plus fontSize="small" />
                        &nbsp;
                        {translateString('Add')}
                      </Button>
                    </span>
                  )}
                  {isBatchDocuments && (
                    <span ui-ta={getUITA('FormCtrl', 'SortPrintOrderButton')}>
                      <Button
                        className={classes.sortPrintOrderButton}
                        color="primary"
                        variant="secondary"
                        disabled={!rowsLength}
                        onClick={() => setOpenSortPrintOrderModal(true)}
                      >
                        <ListNumber fontSize="small" />
                        &nbsp;
                        {translateString('Sort Print Order')}
                      </Button>
                    </span>
                  )}
                  {isBatchDocuments && (
                    <span ui-ta={getUITA('FormCtrl', 'AddDocumentButton')}>
                      <Button
                        color="primary"
                        variant="secondary"
                        onClick={() => setOpenAddDocumentModal(true)}
                      >
                        <Plus fontSize="small" />
                        &nbsp;
                        {translateString('Add Document')}
                      </Button>
                    </span>
                  )}
                </Grid>
              )}
              <Grid style={{ overflowX: 'auto' }} item xs={12} id={instanceId}>
                <div
                  ref={containerRef}
                  className={classNames(
                    classes.root,
                    classes.rowSpacing,
                    rowsLength === 0 ? classes.emptyTable : ''
                  )}
                >
                  {rowsLength ? (
                    <Table
                      rows={
                        !subgridId
                          ? rows.map((row) => ({
                              ...row,
                              disableDelete: disableDeleteFilter[row[rowIndexKey]],
                              enableCopy: enableCopyFilter[row[rowIndexKey]],
                              disableEdit: disableEditFilter[row[rowIndexKey]],
                              entityName,
                              editAllRows: isTrue(editAllRows),
                              isEditMode: getIsEditMode({
                                editAllRows: isTrue(editAllRows),
                                editedRows,
                                row,
                              }),
                            }))
                          : rows.map((row, i) => ({
                              ...row,
                              isComplexTable: true,
                              expanded: useExpandableRows
                                ? expandedRows.includes(`${row[rowIndexKey]}`)
                                : selectedSubGrids.some(({ rowId }) => rowId === row.key),
                              disableDelete: disableDeleteFilter[row[rowIndexKey]],
                              enableCopy: enableCopyFilter[row[rowIndexKey]],
                              isViewIconShown:
                                isDetail || disableEditFilter[row[rowIndexKey]] || isViewOnly,
                              isDetail,
                              entityName,
                              useExpandableRows,
                              rowsLength,
                              uiRowIndex: i,
                              className: classNames(
                                (expandedRows.includes(`${row[rowIndexKey]}`) ||
                                  selectedSubGrids.some(({ rowId }) => rowId === row.key)) &&
                                  classes.selected
                              ),
                            }))
                      }
                      columns={columnsWithCustomCells || []}
                      rowProps={{ 'ui-ta': 'grid-row' }}
                      page={page}
                      hidePagination={!enablePagination || process.env.NODE_ENV === 'test'} // remove pagination in tests
                      emptyProps={{
                        text: translateString('No data to display'),
                      }}
                      {...sortProps}
                      showFilterIcon={!isFormChangesMode}
                      filterTooltipText={translateString('Filter')}
                      sortTooltipText={translateString('Sort')}
                      onChange={() => {
                        setInvalidateRows(true);
                      }}
                      onToggleFilters={(open) => {
                        open && setInvalidateRows(true);
                        isFiltersShownRef.current = open;
                      }}
                      ExpandableComponent={useExpandableRows ? expandableComponent : undefined}
                      tablePaginationProps={
                        enablePagination ? getTablePaginationProps(translateString) : {}
                      }
                      ref={tableRowHeadRef}
                    />
                  ) : (
                    <Table
                      columns={columnsWithCustomCells}
                      rows={rows}
                      emptyProps={{
                        text: translateString('No data to display'),
                      }}
                      sortTooltipText={translateString('Sort')}
                      hidePagination
                      {...sortProps}
                    />
                  )}
                </div>
              </Grid>
            </Grid>
          </Accordion>
        </SSGridContext.Provider>
      </div>
      <AttachmentModal
        open={openAttachmentModal}
        onClose={() => setOpenAttachmentModal(false)}
        entityName={entityName}
        gridId={id}
        parentData={isChildAttachment ? { parentEntityName, parentRowId } : null}
      />
      {isBatchDocuments && (
        <SortPrintOrderModal
          open={openSortPrintOrderModal}
          onClose={(isSaved) => {
            setOpenSortPrintOrderModal(false);
            isSaved && setInvalidateRows(true);
          }}
          originalRows={rows}
          entityName={entityName}
        />
      )}
      {isBatchDocuments && (
        <AddDocumentModal
          open={openAddDocumentModal}
          onClose={() => setOpenAddDocumentModal(false)}
          entityName={entityName}
          index={index}
          highestPrintOrderNubmer={highestPrintOrderNubmer}
          existingRows={rows}
        />
      )}
    </>
  );
};

export default React.memo(SSRuntimeGrid);
